import {call, cancelled, fork, put, select} from "redux-saga/effects";
import {randomInt} from "./random";
import {IApplicationState} from "../../store";
import {delay} from "redux-saga";
import {trackError} from "../../store/log/actions";
import {LogLevel} from "../../store/log/types";
import {endMetric, startMetric} from "../../store/log/performance";
import {IApiSettings} from "../../store/settings/types";
import {compress} from "./compress";
import callOnIdle from "./callOnIdle";
import TimeoutError from "./timeoutError";

let currentEndpoint = randomInt(100);
export const sessionId = window.sessionId;

const defaultSerializer = (data: any) => data
    ? JSON.stringify(data)
    : undefined;

export interface IApiRequest {
    params: {
        section: string;
        method: string;
        [key: string]: any
    };
    data?: any;
    serializer?: (data: any) => string;
    retryCount?: number;
    requestTimeout?: number;
    trackPerformance?: boolean;
}

export interface IRequest {
    method: string;
    mode: "cors";
    credentials: "include";
    headers: Record<string, string>;
    body?: Uint8Array | string;
    signal?: AbortSignal;
}

export interface IApiResponse {
    success: boolean;
}

export function buildUrl(apiEndpoint: string, apiUriTemplate: string, params: Record<string, any>) {
    apiEndpoint += apiUriTemplate;

    const addParam = (key: string, value: any) => {
        const urlParam = encodeURIComponent(key) + "=" + encodeURIComponent(value);

        apiEndpoint = apiEndpoint.includes("?")
            ? apiEndpoint += "&" + urlParam
            : apiEndpoint += "?" + urlParam;
    };

    for (let [key, value] of Object.entries(params)) {
        const replacement = `{${key.toLowerCase()}}`;
        if (apiEndpoint.includes(replacement)) {
            apiEndpoint = apiEndpoint.replace(replacement, encodeURIComponent(value));
        } else {
            if (Array.isArray(value)) {
                for (let item of value) {
                    addParam(key, item);
                }
            } else {
                addParam(key, value);
            }
        }
    }

    return apiEndpoint;
}

export function buildRequest(signature: string, postParams?: any, serializer = defaultSerializer): IRequest {
    return {
        method: postParams ? "POST" : "GET",
        mode: "cors",
        credentials: "include",
        headers: {
            "Authorization": "Bearer " + signature,
            "X-Js-Ses": sessionId,
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        body: serializer(postParams),
    }
}

export function getCurrentApiEndpoint(endpoints: string[]) {
    return endpoints[currentEndpoint % endpoints.length];
}

export default function* apiCall(requestInfo: IApiRequest) {
    let taskCancellation = new AbortController();

    try {
        const {api, signature}: { api: IApiSettings, signature: string } = yield select(({ settings, player }: IApplicationState) => ({
            api: settings.api,
            signature: player.signature,
        }));

        requestInfo = {
            trackPerformance: true,
            retryCount: api.retryCount,
            requestTimeout: api.requestTimeout,
            ...requestInfo
        };

        let request = buildRequest(signature, requestInfo.data, requestInfo.serializer);

        const compressRequestBody = api.gzipEnabled
            && request.body
            && request.body.length > api.gzipMinLength;
        if (compressRequestBody) {
            request = {
                ...request,
                headers: {
                    ...request.headers,
                    ...(compressRequestBody
                        ? { "Content-Encoding": "gzip" }
                        : undefined),
                },
                body: compressRequestBody
                    ? yield call(compress, request.body, api.gzipLevel, requestInfo.trackPerformance)
                    : request.body
            };
        }

        let currentEndpointIndex = currentEndpoint;
        let currentDelay = api.retryTimeout;
        let lastError;

        const registerError = function* (message: string, url: string, data?: object) {
            yield put(trackError({
                logLevel: LogLevel.Warn,
                category: "ApiCall",
                message: "Request timeout",
                data: {
                    url,
                    request: {
                        ...request,
                        body: undefined,
                        signal: undefined,
                        timeout: requestInfo.requestTimeout,
                    },
                    ...data
                }
            }));
        };

        for (let i = 0; i < requestInfo.retryCount!; i++) {
            const url = buildUrl(api.endpoints[currentEndpointIndex % api.endpoints.length], api.uriTemplate, requestInfo.params);

            const metricId = requestInfo.trackPerformance
                ? yield call(startMetric, "apiCall", `${requestInfo.params.section}_${requestInfo.params.method}`)
                : 0;

            try {
                request.signal = taskCancellation.signal;

                const response = yield callOnIdle(() => Promise.race([
                    fetch(url, request),
                    new Promise((_, reject) => setTimeout(() => reject(new TimeoutError()), requestInfo.requestTimeout))
                ]));

                if (response.ok) {
                    const content: IApiResponse = yield call([response, response.json]);
                    if (content.success) {
                        return content;
                    } else {
                        lastError = "Invalid api response";
                        break;
                    }
                }

                lastError = "Api request failed";

                yield fork(registerError, lastError, url, { response });
            } catch (error) {
                if (error instanceof TimeoutError) {
                    taskCancellation.abort();
                    taskCancellation = new AbortController();

                    lastError ="Request timeout";
                } else {
                    lastError = "An error has occurred";
                }

                yield fork(registerError, lastError, url);
            } finally {
                if (requestInfo.trackPerformance) {
                    yield fork(endMetric, metricId);
                }
            }

            yield delay(currentDelay);
            currentDelay = Math.min(api.maxRetryTimeout, 2 * currentDelay);
            if (currentEndpointIndex === currentEndpoint) {
                currentEndpointIndex = currentEndpoint = currentEndpoint + 1;
            }
        }

        throw new Error(lastError);
    }
    finally {
        if (yield cancelled()) {
            taskCancellation.abort();
        }
    }
}